import { Observable } from 'rxjs';
import {
  TuiCheckboxModule,
  TuiComboBoxModule,
  TuiFilterByInputPipeModule,
  TuiMultiSelectModule,
  TuiSelectModule,
} from '@taiga-ui/kit';
import { TUI_IS_MOBILE_RES, TuiDataListModule, TuiLoaderModule, TuiTextfieldControllerModule } from '@taiga-ui/core';
import { TuiLetModule } from '@taiga-ui/cdk';
import { TranslateModule } from '@ngx-translate/core';
import { Maybe } from '@iss/libs/graphql';
import { FormControl, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  ContentChild,
  Inject,
  inject,
  Input,
  Self,
} from '@angular/core';
import { AsyncPipe, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';

import { TypeaheadSelectItemDirective } from './typeahead-select-item.directive';
import { TuiComboBoxClearableDirective } from './tui-combo-box-clearable.directive';
import { MultiselectAllComponent } from './multiselect-all';
import { DoNotClearOnSearchDirective } from './do-not-clear-on-search.directive';

import { IsFalsePipe } from '@iss/wo-common/pipes';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'iss-typeahead-select',
  templateUrl: './typeahead-select.component.html',
  standalone: true,
  imports: [
    AsyncPipe,
    DoNotClearOnSearchDirective,
    FormsModule,
    IsFalsePipe,
    MultiselectAllComponent,
    NgForOf,
    NgIf,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TranslateModule,
    TuiCheckboxModule,
    TuiComboBoxClearableDirective,
    TuiComboBoxModule,
    TuiDataListModule,
    TuiFilterByInputPipeModule,
    TuiLetModule,
    TuiLoaderModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiTextfieldControllerModule,
  ],
})
export class TypeaheadSelectComponent<TItem, TValue> {
  public readonly isMobileRes$ = inject(TUI_IS_MOBILE_RES);
  public search: string | null = null;

  @Input({ required: true }) public loading$!: Observable<boolean>;
  @Input({ required: true }) public items$!: Observable<readonly TItem[]>;
  @Input({ required: true }) public placeholder!: string;
  @Input({ required: true }) public stringify!: (items: readonly TItem[] | null) => (value: TValue) => string;
  @Input({ required: true }) public matcher!: ((item: TItem) => string) | any; // TODO: remove any
  @Input({ required: true }) public content!: (value: TValue | 'ALL', items: readonly TItem[] | null) => string | undefined;
  @Input({ required: true }) public identity!: (item: TItem) => TValue;
  @Input() public clearable = true;
  @Input() public withAll: Maybe<boolean>;
  @Input() public readonly: Maybe<boolean>;
  @Input() public forceTypeahead: Maybe<boolean>;
  @Input() public multi: Maybe<boolean>;
  @Input() public loading: Maybe<boolean>;

  @ContentChild(TypeaheadSelectItemDirective, { static: false }) public itemDirective?: TypeaheadSelectItemDirective<TItem, TValue>;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl<TValue | readonly TValue[] | 'ALL'>>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  get isEmpty(): boolean {
    return Array.isArray(this.control.control.value) ? !this.control.control.value.length : !this.control.control.value;
  }

  public onFocusChange(focused: boolean): void {
    if (!focused) this.search = null;
  }
}
